// src/pages/ReviewSession.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { fetchSessionReview, fetchAiReview } from '../api/api'; // Corrected to fetchSessionReview
import QuestionDetailModal from '../components/QuestionDetailModal';
import ReactMarkdown from 'react-markdown'; // Import ReactMarkdown
import remarkGfm from 'remark-gfm';
import { useContext } from 'react';

import { SubscriptionContext } from '../SubscriptionContext';
import { useNavigate } from 'react-router-dom';


import './ReviewSession.css'; // Import the CSS for this page

const ReviewSession = () => {
  const { sessionId } = useParams(); // Get session ID from URL (e.g., /review/abc123xyz)
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiReview, setAiReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [hasAttemptedAutoGeneration, setHasAttemptedAutoGeneration] = useState(false);

  // State for the question detail modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const { userSubscription, createCheckoutSession } = useContext(SubscriptionContext);

  // Effect to fetch session details when the component mounts or sessionId changes
  useEffect(() => {
    const getSessionDetails = async () => {
      setLoading(true);
      setError(null); // Clear previous errors
      try {
        const data = await fetchSessionReview(sessionId); // Call the API to get session data
        setSessionData(data);
        if (data && data.ai_review) {
          setAiReview(data.ai_review);
        }
        
      } catch (err) {
        console.error("Failed to fetch session review details:", err);
        setError("Failed to load session review. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) { // Only fetch if sessionId is available
      getSessionDetails();
    }
  }, [sessionId]); // Dependency array: re-run if sessionId changes

  const navigate = useNavigate();
  useEffect(() => {
    const handlePopState = () => {
        navigate('/dashboard', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup function to remove the event listener
    return () => {
        window.removeEventListener('popstate', handlePopState);
    };
}, [navigate]);

  const handleGenerateReview = async () => {
    if (userSubscription?.subscription_status !== 'active' && userSubscription.subscription_status != 'trialing') {
      setError("You need a Premium subscription to generate AI reviews.");
      return;
    }
    if (aiReview) {
      setError("AI review already exists for this session.");
      return;
    }
    
    setReviewLoading(true); // <--- START LOADING
    setError(null);
    
    try {
      const reviewData = await fetchAiReview(sessionId);
      setAiReview(reviewData.ai_session_review); // Assume this is a string. If it's an object, stringify it
    } catch (err) {
      console.error("Failed to get AI session review:", err);
      setError("Failed to generate AI session review.");
    } finally {
      setReviewLoading(false); // <--- STOP LOADING
    }
  };
  // Effect to automatically generate review when component mounts and conditions are met
  useEffect(() => {
    // Only auto-generate if:
    // 1. Session data is loaded
    // 2. User has standard subscription
    // 3. No AI review exists yet
    // 4. Haven't already attempted auto-generation
    // 5. Not currently loading a review
    if (sessionData && 
        (userSubscription?.subscription_status === 'active' || userSubscription?.subscription_status === 'trialing') && 
        !aiReview && 
        !hasAttemptedAutoGeneration &&
        !reviewLoading) {
      setHasAttemptedAutoGeneration(true);
      handleGenerateReview();
    }
  }, [sessionData, userSubscription, aiReview, hasAttemptedAutoGeneration, reviewLoading]); // Dependencies to control when this runs
  
  // Handler to open the modal and set the initial question index
  const handleViewQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setIsModalOpen(true);
  };

  // Handler to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handler to navigate to the next question in the modal
  const handleNextQuestion = () => {
    if (sessionData && currentQuestionIndex < sessionData.completed_questions.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    }
  };

  // Handler to navigate to the previous question in the modal
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    }
  };

  // Memoized calculation of total and correct questions for the summary
  const totalQuestions = sessionData?.completed_questions?.length || 0;
  const correctQuestions = useMemo(() => {
    return sessionData?.completed_questions?.filter(q => q.is_correct).length || 0;
  }, [sessionData]); // Re-calculate if sessionData changes

  // --- Conditional Rendering for Loading, Error, No Data ---
  if (loading) {
    return (
      <div className="page-content review-page">
        <p className="loading-message">Loading session details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content review-page">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="page-content review-page">
        <p className="no-data-message">No session data found for this ID.</p>
      </div>
    );
  }
  const canGenerateAIReview = userSubscription?.subscription_status === 'active';

  // Determine if the button should be disabled
  const isButtonDisabled = reviewLoading || !canGenerateAIReview || !!aiReview;

  // Only show the button section if no AI review has been generated yet
  const showGenerateButtonSection = !aiReview;

  // NEW: Condition for showing the upgrade link
  const showUpgradeLink = showGenerateButtonSection && !canGenerateAIReview;
  // --- Main Review Page Content ---
  return (
    <div className="page-content review-page">
      <h2>Review Session: {sessionId}</h2>
      {showGenerateButtonSection && (
        <div className="generate-review-section">
          <button
            className={`generate-session-review ${isButtonDisabled ? 'disabled' : ''}`}
            onClick={handleGenerateReview}
            disabled={isButtonDisabled}
          >
            {reviewLoading ? "Generating Review..." : "Generate AI Review for Session"}
          </button>
          
          {showUpgradeLink && (
            <div className="upgrade-link-container">
              <span className="upgrade-prompt">
                {userSubscription?.subscription_tier === 'free' ? 'Not on Standard or Premium?' : 'Upgrade to generate AI reviews!'}
              </span>
              <button
                // Direct to Basic/Standard plan for upgrade
                onClick={() => createCheckoutSession('standard')}
                className="upgrade-review-button"
              >
                Upgrade Account
              </button>
            </div>
          )}

        </div>
      )}
      {/* Session Summary Overview */}
      <div className="session-overview">
        <h3>Session Summary</h3>
        <div className="ai-review-container">
        <h3>AI-Generated Review</h3>
        {aiReview ? (
          <>
            <div className="ai-review-preview"><ReactMarkdown remarkPlugins={[remarkGfm]}>{aiReview}</ReactMarkdown></div>
            <button className="see-more-btn" onClick={() => setIsReviewModalOpen(true)}>
              See Full Review
            </button>
          </>
        ) : (
          <p>No review has been generated yet for this session.</p>
        )}
      </div>

        <p><strong>Date:</strong> {sessionData.timestamp ? new Date(sessionData.timestamp).toLocaleString() : 'N/A'}</p>
        <p><strong>Difficulty:</strong> {sessionData.difficulty}</p>
        <p><strong>Skills:</strong> {sessionData.skills ? sessionData.skills.join(', ') : 'N/A'}</p>
        <p>
          <strong>Score:</strong> {correctQuestions} / {totalQuestions} Correct
          ({totalQuestions > 0 ? ((correctQuestions / totalQuestions) * 100).toFixed(0) : 0}%)
        </p>
      </div>

      {/* List of Questions */}
      <h3>Questions Overview</h3>
      {totalQuestions === 0 ? (
        <p className="no-data-message">No questions found for this session.</p>
      ) : (
        <ul className="question-list">
{sessionData.completed_questions
  .map((question, index) => (
    <li key={question.id || index} className="question-list-item">
      <div className="question-summary">
        <strong>Question {index + 1}:</strong> {question.question.substring(0, 80)}...
        <br />
        Your Answer: <span className={question.is_correct ? 'correct' : 'incorrect'}>
          {question.selected_answer || 'N/A'}
        </span>
      </div>
      <span className={`question-status-indicator ${question.is_correct ? 'correct' : 'incorrect'}`}>
        {question.is_correct ? 'Correct' : 'Incorrect'}
      </span>
      <button
        className="view-question-btn"
        onClick={() => handleViewQuestion(index)}
      >
        View Details
      </button>
    </li>
))}
        </ul>
      )}

      {/* Question Detail Modal (conditionally rendered) */}
      {isModalOpen && sessionData && sessionData.completed_questions && (
        <QuestionDetailModal
          question={sessionData.completed_questions[currentQuestionIndex]}
          totalQuestions={totalQuestions}
          currentQuestionIndex={currentQuestionIndex}
          onClose={handleCloseModal}
          onNext={handleNextQuestion}
          onPrev={handlePrevQuestion}
        />
      )}

      {isReviewModalOpen && (
        <div className="review-modal-overlay" onClick={() => setIsReviewModalOpen(false)}>
          <div className="review-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="review-modal-close" onClick={() => setIsReviewModalOpen(false)}>&times;</button>
            <h3>Full AI Review</h3>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiReview}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewSession;
